// useEsgUrlDateRange.js
// react
import { useCallback, useEffect, useMemo } from 'react';

// components
import useSearchParams from '@/hooks/useStableSearchParams';

import { DateTime } from 'luxon';

const ESG_FROM_PARAM = 'esgFrom';
const ESG_TO_PARAM = 'esgTo';

function createDefaultEsgRange({ zone = 'Europe/Copenhagen' } = {}) {
  const nowLocal = DateTime.now().setZone(zone);

  // ESG default:
  //   start: start of last year
  //   end: end of yesterday
  const start = nowLocal.startOf('year').minus({ years: 1 });
  const end = nowLocal.minus({ days: 1 }).endOf('day');

  return [start, end];
}

function parseRangeFromParams({ fromIso, toIso, zone }) {
  const fromParam = fromIso;
  const toParam = toIso;

  if (!fromParam || !toParam) return null;

  const from = DateTime.fromISO(fromParam).setZone(zone);
  const to = DateTime.fromISO(toParam).setZone(zone);

  if (!from.isValid || !to.isValid) return null;

  return [from, to];
}

export function useEsgUrlDateRange(options = {}) {
  const {
    fromKey = ESG_FROM_PARAM,
    toKey = ESG_TO_PARAM,
    zone = 'Europe/Copenhagen',
    defaultDays = 30,
  } = options;

  const [searchParams, setSearchParams] = useSearchParams();
  const fromIso = searchParams.get(fromKey);
  const toIso = searchParams.get(toKey);
  // URL is the source of truth: derive range from URL if valid, else fallback default

  const range = useMemo(() => {
    const parsed = parseRangeFromParams({ fromIso, toIso, zone });
    if (parsed) return parsed;
    return createDefaultEsgRange({ zone, days: defaultDays });
  }, [fromIso, toIso, zone, defaultDays]);

  const [from, to] = range;

  // External sync only: if params are missing/invalid, write defaults to URL (replace: true)
  useEffect(() => {
    const parsed = parseRangeFromParams({ fromIso, toIso, zone });
    if (parsed) return;

    const [start, end] = createDefaultEsgRange({ zone, days: defaultDays });

    const nextFromIso = start.toISO();
    const nextToIso = end.toISO();

    const currentFromIso = fromIso;
    const currentToIso = toIso;

    const same = currentFromIso === nextFromIso && currentToIso === nextToIso;
    if (same) return;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(fromKey, nextFromIso);
        next.set(toKey, nextToIso);
        return next;
      },
      { replace: true },
    );
  }, [fromIso, toIso, zone, defaultDays, fromKey, toKey, setSearchParams]);

  // Update function: write to URL; derived values update automatically
  const setRange = useCallback(
    (nextFrom, nextTo, { roundToDay = true, clampToNow = true } = {}) => {
      if (!nextFrom || !nextTo) return;

      let start = nextFrom.setZone(zone);
      let end = nextTo.setZone(zone);

      if (roundToDay) {
        start = start.startOf('day');
        end = end.endOf('day');
      }

      const now = DateTime.now().setZone(zone);
      if (clampToNow && end > now) {
        end = now;
      }

      if (end < start) return;

      const nextFromIso = start.toISO();
      const nextToIso = end.toISO();

      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        const currentFromIso = prev.get(fromKey);
        const currentToIso = prev.get(toKey);

        const same =
          currentFromIso === nextFromIso && currentToIso === nextToIso;
        if (same) return;
        next.set(fromKey, nextFromIso);
        next.set(toKey, nextToIso);
        return next;
      });
    },
    [setSearchParams, fromKey, toKey, zone],
  );

  // Derived values: UTC millis + duration
  const timeRange = useMemo(() => {
    if (!from || !to) return null;
    return {
      start: from.toUTC().toMillis(),
      end: to.toUTC().toMillis(),
    };
  }, [from, to]);

  const durationDays = useMemo(() => {
    if (!from || !to) return null;
    return Math.round(to.diff(from, 'days').days);
  }, [from, to]);

  return {
    from,
    to,
    range,
    setRange,
    timeRange,
    durationDays,
  };
}
// // useEsgUrlDateRange.js
// import { useCallback, useEffect, useMemo, useState } from 'react';
// import { useSearchParams } from 'react-router-dom';
// import { DateTime } from 'luxon';

// export const ESG_FROM_PARAM = 'esgFrom';
// export const ESG_TO_PARAM = 'esgTo';

// function createDefaultEsgRange({ zone = 'Europe/Copenhagen' } = {}) {
//   const nowLocal = DateTime.now().setZone(zone);

//   // ESG default:
//   //   start: start of last year
//   //   end: end of yesterday
//   const start = nowLocal.startOf('year').minus({ years: 1 });
//   const end = nowLocal.minus({ days: 1 }).endOf('day');

//   return [start, end];
// }

// export function useEsgUrlDateRange(options = {}) {
//   const {
//     fromKey = ESG_FROM_PARAM,
//     toKey = ESG_TO_PARAM,
//     zone = 'Europe/Copenhagen',
//   } = options;

//   const [searchParams, setSearchParams] = useSearchParams();

//   // --- Initial range: try from ESG URL params, else ESG default ---
//   const [range, setRangeState] = useState(() => {
//     const fromParam = searchParams.get(fromKey);
//     const toParam = searchParams.get(toKey);

//     if (fromParam && toParam) {
//       const from = DateTime.fromISO(fromParam).setZone(zone);
//       const to = DateTime.fromISO(toParam).setZone(zone);

//       if (from.isValid && to.isValid) {
//         return [from, to];
//       }
//     }

//     return createDefaultEsgRange({ zone });
//   });

//   // --- Sync with URL changes ---
//   useEffect(() => {
//     const fromParam = searchParams.get(fromKey);
//     const toParam = searchParams.get(toKey);

//     const setDefaultRangeInUrl = () => {
//       const [start, end] = createDefaultEsgRange({ zone });
//       setRangeState([start, end]);
//       const next = new URLSearchParams(searchParams);
//       next.set(fromKey, start.toISO());
//       next.set(toKey, end.toISO());
//       setSearchParams(next, { replace: true });
//     };

//     if (!fromParam || !toParam) {
//       const [currentFrom, currentTo] = range || [];

//       const [fallbackFrom, fallbackTo] =
//         currentFrom && currentTo
//           ? [currentFrom, currentTo]
//           : createDefaultEsgRange({ zone });

//       const next = new URLSearchParams(searchParams);
//       next.set(fromKey, fallbackFrom.toISO());
//       next.set(toKey, fallbackTo.toISO());
//       setSearchParams(next, { replace: true });
//       return;
//     }

//     const from = DateTime.fromISO(fromParam).setZone(zone);
//     const to = DateTime.fromISO(toParam).setZone(zone);

//     if (!from.isValid || !to.isValid) {
//       setDefaultRangeInUrl();
//       return;
//     }

//     setRangeState((prev) => {
//       const [prevFrom, prevTo] = prev || [];
//       const isSame =
//         prevFrom &&
//         prevTo &&
//         prevFrom.toISO() === from.toISO() &&
//         prevTo.toISO() === to.toISO();

//       return isSame ? prev : [from, to];
//     });
//   }, [searchParams, setSearchParams, fromKey, toKey, zone, range]);

//   // --- Function to update ESG range & URL ---
//   const setRange = useCallback(
//     (nextFrom, nextTo, { roundToDay = true, clampToNow = true } = {}) => {
//       if (!nextFrom || !nextTo) return;

//       let start = nextFrom;
//       let end = nextTo;

//       if (roundToDay) {
//         start = start.startOf('day');
//         end = end.endOf('day');
//       }

//       const now = DateTime.now().setZone(zone);
//       if (clampToNow && end > now) {
//         end = now;
//       }

//       if (end < start) return;

//       setRangeState([start, end]);

//       const next = new URLSearchParams(searchParams);
//       next.set(fromKey, start.toISO());
//       next.set(toKey, end.toISO());
//       setSearchParams(next);
//     },
//     [searchParams, setSearchParams, fromKey, toKey, zone],
//   );

//   const [from, to] = range;

//   // --- Derived values: UTC millis + duration ---
//   const timeRange = useMemo(() => {
//     if (!from || !to) return null;
//     return {
//       start: from.toUTC().toMillis(),
//       end: to.toUTC().toMillis(),
//     };
//   }, [from, to]);

//   const durationDays = useMemo(() => {
//     if (!from || !to) return null;
//     return Math.round(to.diff(from, 'days').days);
//   }, [from, to]);

//   return {
//     from,
//     to,
//     range,
//     setRange, // (fromDateTime, toDateTime, options?)
//     timeRange, // { start, end } in UTC millis
//     durationDays, // integer
//   };
// }
